import { createTheme, responsiveFontSizes, alpha, Theme } from '@mui/material/styles';

/**
 * Nadeem design tokens, derived from the logo:
 *  lavender #ADA2FF  ·  sky #ADD4FF  ·  navy #015287
 * plus a few warm, playful accents for children's content.
 */
export const brand = {
  lavender: '#ADA2FF',
  lavenderDeep: '#7B6CFF',
  lavenderSoft: '#E9E6FF',
  sky: '#ADD4FF',
  skyDeep: '#5FB0FF',
  skySoft: '#E6F2FF',
  navy: '#015287',
  navyDeep: '#023B61',
  mint: '#8FE6C9',
  peach: '#FFBF96',
  sun: '#FFDD7A',
  rose: '#FFB3C7',
  ink: '#1B1F3B',
  inkSoft: '#5B6079',
  bgLight: '#F7F6FF',
  bgDark: '#0E1226',
  paperDark: '#161B33',
} as const;

/**
 * Historically these were gradients. The design is flat now, so each token is a
 * single solid colour; the names stay so every usage site keeps working.
 */
export const gradients = {
  brand: brand.navy,
  soft: brand.lavenderSoft,
  sunrise: '#FFE3CF',
  mint: '#D6F5EA',
  navy: brand.navy,
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    brand: typeof brand;
    gradients: typeof gradients;
  }
  interface PaletteOptions {
    brand?: typeof brand;
    gradients?: typeof gradients;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    gradient: true;
    soft: true;
  }
}

export type Mode = 'light' | 'dark';
export type Direction = 'ltr' | 'rtl';

export const createAppTheme = (mode: Mode, direction: Direction, highContrast = false): Theme => {
  const isDark = mode === 'dark';
  const latin = "'Nunito', system-ui, -apple-system, 'Segoe UI', sans-serif";
  const arabic = "'Tajawal', 'Nunito', system-ui, sans-serif";
  const fontFamily = direction === 'rtl' ? arabic : latin;

  const textPrimary = isDark ? '#EEF0FF' : highContrast ? '#000000' : brand.ink;
  const textSecondary = isDark ? alpha('#EEF0FF', 0.7) : highContrast ? '#222' : brand.inkSoft;

  let theme = createTheme({
    direction,
    palette: {
      mode,
      brand,
      gradients,
      primary: {
        main: isDark ? brand.lavender : brand.navy,
        light: brand.lavender,
        dark: isDark ? '#8F84FF' : brand.navyDeep,
        contrastText: isDark ? brand.ink : '#ffffff',
      },
      secondary: {
        main: isDark ? brand.sky : brand.skyDeep,
        light: brand.sky,
        dark: '#3D8FE0',
        contrastText: isDark ? brand.ink : '#ffffff',
      },
      info: { main: brand.navy, light: '#3A7BAA', dark: brand.navyDeep, contrastText: '#fff' },
      success: { main: '#3FC79A', light: brand.mint, dark: '#2A9C77', contrastText: '#0B2A20' },
      warning: { main: '#F6B73C', light: brand.sun, dark: '#C98F1B', contrastText: '#2B1F00' },
      error: { main: '#F0648A', light: brand.rose, dark: '#C43F67', contrastText: '#fff' },
      background: {
        default: isDark ? brand.bgDark : highContrast ? '#FFFFFF' : brand.bgLight,
        paper: isDark ? brand.paperDark : '#FFFFFF',
      },
      text: { primary: textPrimary, secondary: textSecondary },
      divider: isDark ? alpha('#EEF0FF', 0.1) : alpha(brand.ink, 0.08),
    },
    shape: { borderRadius: 4 },
    typography: {
      fontFamily,
      h1: { fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.05, fontSize: '4.25rem' },
      h2: { fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1, fontSize: '3rem' },
      h3: { fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '2.25rem' },
      h4: { fontWeight: 800, lineHeight: 1.2, fontSize: '1.75rem' },
      h5: { fontWeight: 700, lineHeight: 1.3, fontSize: '1.35rem' },
      h6: { fontWeight: 700, lineHeight: 1.35, fontSize: '1.1rem' },
      subtitle1: { fontWeight: 600, fontSize: '1.05rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.9rem' },
      body1: { fontSize: '1.05rem', lineHeight: 1.7 },
      body2: { fontSize: '0.95rem', lineHeight: 1.65 },
      button: { fontWeight: 700, textTransform: 'none', letterSpacing: 0 },
      overline: { fontWeight: 800, letterSpacing: '0.16em', fontSize: '0.72rem' },
    },
    shadows: [
      'none',
      `0 1px 2px ${alpha(brand.navy, 0.06)}`,
      `0 2px 6px ${alpha(brand.navy, 0.06)}`,
      `0 4px 12px ${alpha(brand.navy, 0.07)}`,
      `0 6px 18px ${alpha(brand.navy, 0.08)}`,
      `0 8px 24px ${alpha(brand.navy, 0.09)}`,
      `0 10px 30px ${alpha(brand.navy, 0.04)}`,
      `0 12px 34px ${alpha(brand.navy, 0.05)}`,
      `0 14px 38px ${alpha(brand.navy, 0.06)}`,
      `0 16px 42px ${alpha(brand.navy, 0.06)}`,
      `0 18px 46px ${alpha(brand.navy, 0.07)}`,
      `0 20px 50px ${alpha(brand.navy, 0.08)}`,
      `0 22px 54px ${alpha(brand.navy, 0.08)}`,
      `0 24px 58px ${alpha(brand.navy, 0.09)}`,
      `0 26px 62px ${alpha(brand.navy, 0.10)}`,
      `0 28px 66px ${alpha(brand.navy, 0.10)}`,
      `0 30px 70px ${alpha(brand.navy, 0.11)}`,
      `0 32px 74px ${alpha(brand.navy, 0.12)}`,
      `0 34px 78px ${alpha(brand.navy, 0.13)}`,
      `0 36px 82px ${alpha(brand.navy, 0.13)}`,
      `0 38px 86px ${alpha(brand.navy, 0.14)}`,
      `0 40px 90px ${alpha(brand.navy, 0.15)}`,
      `0 42px 94px ${alpha(brand.navy, 0.15)}`,
      `0 44px 98px ${alpha(brand.navy, 0.16)}`,
      `0 46px 102px ${alpha(brand.navy, 0.16)}`,
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: isDark ? brand.bgDark : brand.bgLight },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 999,
            paddingInline: 22,
            paddingBlock: 10,
            fontSize: '0.98rem',
            transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s, background .25s',
            '&:hover': { transform: 'translateY(-2px)' },
            '&:active': { transform: 'translateY(0) scale(.98)' },
          },
          sizeLarge: { paddingInline: 30, paddingBlock: 14, fontSize: '1.05rem' },
          sizeSmall: { paddingInline: 16, paddingBlock: 7, fontSize: '0.88rem' },
          containedPrimary: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
        },
        variants: [
          {
            props: { variant: 'gradient' },
            style: {
              // kept the variant name; it is now the primary solid button
              color: isDark ? brand.ink : '#fff',
              backgroundColor: isDark ? brand.lavender : brand.navy,
              '&:hover': { backgroundColor: isDark ? '#BFB6FF' : brand.navyDeep },
            },
          },
          {
            props: { variant: 'soft' },
            style: {
              color: isDark ? brand.lavender : brand.navy,
              backgroundColor: isDark ? alpha(brand.lavender, 0.14) : brand.lavenderSoft,
              '&:hover': { backgroundColor: isDark ? alpha(brand.lavender, 0.22) : '#DDD8FF' },
            },
          },
        ],
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha(brand.ink, 0.05)}`,
          },
          rounded: { borderRadius: 24 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { border: 'none', backgroundColor: 'transparent', boxShadow: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            transition: 'transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, fontWeight: 700 },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.7),
            '& fieldset': { borderColor: isDark ? alpha('#fff', 0.12) : alpha(brand.ink, 0.1), borderWidth: 2 },
            '&:hover fieldset': { borderColor: brand.lavender },
            '&.Mui-focused fieldset': { borderColor: brand.navy, borderWidth: 2 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 12, fontWeight: 600, backgroundColor: brand.navy, padding: '8px 12px' },
          arrow: { color: brand.navy },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 28 } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 700, borderRadius: 999, minHeight: 44 } },
      },
      MuiTabs: {
        styleOverrides: { indicator: { height: 3, borderRadius: 3 } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 999, height: 8 }, bar: { borderRadius: 999 } },
      },
      MuiAvatar: {
        styleOverrides: { root: { fontWeight: 800 } },
      },
      MuiSkeleton: {
        styleOverrides: { root: { borderRadius: 16 } },
      },
    },
  });

  theme = responsiveFontSizes(theme, { factor: 2.4 });
  return theme;
};
